import { useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { ShopOverlayRoutes } from '@givebacks/shop';
import { ShopRoutes } from '@givebacks/shop/routes';
import { RouteWithHeaderBackButton } from 'core/Hooks';
import { Experiment_Routes, Experiment_ViewOffersModal } from 'experiments';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import Helmet from '../components/Helmet/Helmet';
import { useOpenGlobalOnboarding } from '../hooks/useOpenGlobalOnboarding';
import usePlatformService from '../hooks/usePlatformService';
import { CauseRoutes } from '../pages/Causes/routes';
import SimpleDonation from '../pages/Donate/SimpleDonation/SimpleDonation';
import { Profile2 } from '../pages/Profile/Profile';
import { Referrals } from '../pages/Referrals/Referrals';
import { Settings } from '../pages/Settings/Settings';
import { ShortSessionCheckout } from '../pages/ShortSessions/Donate';
import { DonationManagement } from '../pages/ShortSessions/DonationManagement';
import { TicketPage } from '../pages/Tickets/TicketPage';
import { TicketsPage } from '../pages/Tickets/TicketsPage';
import { ConfirmRoutes } from '../pages/confirm/routes';
import { AuthLayout } from '../pages/onboarding/layouts/AuthLayout';
import { OnboardingLayout } from '../pages/onboarding/layouts/OnboardingLayout';
import { OnboardingRoutes } from '../pages/onboarding/routes';
import { PasswordReset } from '../pages/passwordReset/PasswordReset';
import { StudentsFeesRoutes } from '../pages/student-fees/routes';
import chromeExtension from '../utils/chromeExtension';
import { ShortSessions } from './Containers/ShortSessions';
import { InAppShortSessionsLayout } from './Layouts/InAppShortSessionsLayout';
import { JoinLayout } from './Layouts/JoinLayout';
import { AuthenticatedLayout } from './Layouts/StandardLayout/AuthenticatedLayout';
import { StandardLayout } from './Layouts/StandardLayout/StandardLayout';
import { AccountRoutes } from './Routes/Account.routes';
import { StoreProviderLayout } from './Routes/CauseRoutes';
import { CommunicationRoutes } from './Routes/CommunicationRoutes';
import { RedirectLegacyEarnRoutes } from './Routes/Earn.routes';
import { HomeRoutes } from './Routes/HomeRoutes';
import { JoinRoutes } from './Routes/JoinRoutes';
import { OverLayRoutes, overlayRoutes } from './Routes/OverLayRoutes';
import { PostCheckoutRedirectRoutes } from './Routes/PostCheckoutRedirectRoutes';
import { RedirectLegacyStoreRoutes } from './Routes/RedirectLegacyStoreRoutes';
import { Redirects } from './Routes/Redirects';
import { RewardsRoutes } from './Routes/Rewards.routes';
import { AuthShortSessionRoute, WalletAddCard } from './Routes/ShortSessionRoutes';
import { WalletRoutes } from './Routes/Wallet.routes';

export const RoutesPaths = () => {
  const Platform = usePlatformService();
  const { isAuthenticated } = useAuthentication();
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location; overlayRoute?: string };

  const backgroundLocation = useMemo(
    () => state?.backgroundLocation || false,
    [state?.backgroundLocation]
  );

  const showOverlayRoutes = useMemo(
    () => backgroundLocation && overlayRoutes.some((route) => location.pathname.includes(route)),
    [backgroundLocation, location.pathname]
  );

  return (
    <>
      <Helmet />

      <Routes location={backgroundLocation || location}>
        <Route element={<OnboardingLayout />}>
          <Route path="/onboarding/*" element={<OnboardingRoutes />} />
          <Route element={<AuthLayout />}>
            {/* Note: The fragments are used to sub-nest those routes within the AuthLayout to remain parity with what a file based routing structure would look like */}
            <Route path="/sign-in" element={<></>} />
            <Route path="/sign-up" element={<></>} />
            <Route path="/oauth/:provider" element={<></>} />
          </Route>
        </Route>

        <Route element={<StandardLayout />}>
          {/* Redirect legacy shop/stores routes to cause page structure */}
          <Route path="/shop/stores/*" element={<RedirectLegacyStoreRoutes />} />
          <Route path="/shop/*" element={<ShopRoutes />} />

          {/* General public routes... */}

          <Route path={'/home/*'} element={<HomeRoutes />} />

          <Route path="/post-checkout/*" element={<PostCheckoutRedirectRoutes />} />

          {/* replaces Earn Routes */}
          <Route path={'/rewards/*'} element={<RewardsRoutes />} />

          {/* redirects to Rewards Routes */}
          <Route path={'/earn/*'} element={<RedirectLegacyEarnRoutes />} />

          <Route
            path="tickets"
            element={
              <RouteWithHeaderBackButton
                routeTo={
                  Platform.isNative ? '/account' : undefined
                }
              >
                <TicketsPage />
              </RouteWithHeaderBackButton>
            }
          />
          <Route path="tickets/:ticketId" element={<TicketPage />} />

          <Route path="/wallet/*" element={<WalletRoutes />} />
          <Route path="/causes/*" element={<CauseRoutes />} />
          <Route path="/student-fees/*" element={<StudentsFeesRoutes />} />

          <Route element={<AuthenticatedLayout />}>
            <Route path="/profile/*" element={<Profile2 />} />
            <Route path="/settings/*" element={<Settings />} />
            <Route path="/account/*" element={<AccountRoutes />} />

            <Route
              path="/share"
              element={
                <Navigate
                  to="/share"
                  state={{ backgroundLocation: { ...location, pathname: '/home' } }}
                />
              }
            />
          </Route>

          <Route path="/communication/*" element={<CommunicationRoutes />} />

          <Route path="/confirm/*" element={<ConfirmRoutes />} />
          <Route
            path="/x/*"
            element={<Experiment_Routes useOpenGlobalOnboarding={useOpenGlobalOnboarding} />}
          />
          <Route path="/gift" element={<Navigate to="/x/gift" />} />

          <Route element={<JoinLayout />}>
            <Route path="/referrals/:code/*" element={<Referrals.Peer />} />
            <Route path="/join/:uuid/*" element={<JoinRoutes />} />
          </Route>
        </Route>

        <Route path="/donate/:uuid" element={<SimpleDonation />} />

        <Route element={<ShortSessions />}>
          <Route path="/auth/session" element={<AuthShortSessionRoute />} />
          <Route path={'/wallet/add-card/*'} element={<WalletAddCard />} />

          <Route element={<InAppShortSessionsLayout />}>
            <Route element={<StoreProviderLayout />}>
              <Route path={'/donate/manage/*'} element={<DonationManagement />} />
              <Route path={'/donate'} element={<ShortSessionCheckout />} />
            </Route>
          </Route>
        </Route>

        <Route
          path="chrome-extension"
          element={
            <Navigate
              to={
                isAuthenticated ? '/rewards/online' : '/rewards/online?from=x-onboarding-extension'
              }
            />
          }
        />

        <Route path="/reset-password" element={<PasswordReset />} />

        <Route path="/*" element={<Redirects />} />
      </Routes>

      <Experiment_ViewOffersModal setTrackingCode={chromeExtension.setTrackingCode} />
      {showOverlayRoutes && <OverLayRoutes />}
      <ShopOverlayRoutes />
    </>
  );
};
